import './BakersViewV2.css';
import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import TabBarV2 from './TabBarV2';
import ProgressHeaderV2 from './ProgressHeaderV2';
import DayFilterV2 from './DayFilterV2';
import { CakeCardV2, BreadCardV2 } from './OrderCardV2';
import orderCardStyles from './OrderCardV2.module.css';
import NewOrderAlertV2 from './NewOrderAlertV2';
import { useCakeOrders, useBreadOrders } from '../../hooks';

// Shared audio context (needs user interaction to unlock)
let audioContext = null;

function getAudioContext() {
    if (!audioContext) {
        audioContext = new (window.AudioContext || window.webkitAudioContext)();
    }
    if (audioContext.state === 'suspended') {
        audioContext.resume();
    }
    return audioContext;
}

// Play notification chime
function playNotificationSound() {
    try {
        const ctx = getAudioContext();
        const now = ctx.currentTime;

        // Two-tone chime (C5 and E5)
        [523, 659].forEach((freq, i) => {
            const osc = ctx.createOscillator();
            const gain = ctx.createGain();
            osc.connect(gain);
            gain.connect(ctx.destination);
            osc.frequency.value = freq;
            osc.type = 'sine';
            gain.gain.setValueAtTime(0, now + i * 0.2);
            gain.gain.linearRampToValueAtTime(0.4, now + i * 0.2 + 0.05);
            gain.gain.linearRampToValueAtTime(0, now + i * 0.2 + 0.4);
            osc.start(now + i * 0.2);
            osc.stop(now + i * 0.2 + 0.4);
        });
    } catch (e) {
        console.log('Audio not available:', e);
    }
}

// Unlock audio on first user interaction
function unlockAudio() {
    getAudioContext();
    document.removeEventListener('click', unlockAudio);
    document.removeEventListener('touchstart', unlockAudio);
}

// Get today's date in YYYY-MM-DD format (using local date, not UTC)
function getTodayKey() {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
}

// Calculate pending order counts per day
function getOrderCountsByDate(orders, isPendingFn) {
    const counts = {};
    orders.forEach(order => {
        const dateKey = order.date.split('T')[0];
        if (isPendingFn(order)) {
            counts[dateKey] = (counts[dateKey] || 0) + 1;
        }
    });
    return counts;
}

// Filter orders by date and sort (pending first)
function filterOrdersByDate(orders, dateKey, isPendingFn) {
    const filtered = orders.filter(order => order.date.split('T')[0] === dateKey);
    // Sort: pending first, then by time
    return filtered.sort((a, b) => {
        const aPending = isPendingFn(a);
        const bPending = isPendingFn(b);
        if (aPending !== bPending) return aPending ? -1 : 1;
        return a.time.localeCompare(b.time);
    });
}

export default function BakersViewV2() {
    const [searchParams, setSearchParams] = useSearchParams();
    const [activeTab, setActiveTab] = useState('cakes');
    const [selectedDate, setSelectedDate] = useState(getTodayKey());

    // Unlock audio on first user interaction
    useEffect(() => {
        document.addEventListener('click', unlockAudio);
        document.addEventListener('touchstart', unlockAudio);
        return () => {
            document.removeEventListener('click', unlockAudio);
            document.removeEventListener('touchstart', unlockAudio);
        };
    }, []);

    // Use custom hooks for data fetching
    const { orders: cakeOrders } = useCakeOrders(false);
    const { orders: breadOrders } = useBreadOrders(false);

    // New order notification state
    const [showAlert, setShowAlert] = useState(false);
    const [newOrders, setNewOrders] = useState([]);
    const previousCakeIds = useRef(new Set());
    const previousBreadIds = useRef(new Set());

    // Fullscreen state
    const [isFullscreen, setIsFullscreen] = useState(false);

    // Wake Lock to prevent screen sleep
    useEffect(() => {
        let wakeLock = null;

        const requestWakeLock = async () => {
            try {
                if ('wakeLock' in navigator) {
                    wakeLock = await navigator.wakeLock.request('screen');
                }
            } catch (err) {
                console.log('Wake Lock error:', err);
            }
        };

        requestWakeLock();

        // Re-acquire wake lock when page becomes visible again
        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible') {
                requestWakeLock();
            }
        };
        document.addEventListener('visibilitychange', handleVisibilityChange);

        return () => {
            wakeLock?.release();
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, []);

    // Fullscreen handlers
    useEffect(() => {
        const handleFullscreenChange = () => {
            const isFs = !!document.fullscreenElement;
            setIsFullscreen(isFs);
            // Hide navbar in fullscreen
            document.body.classList.toggle('bakers-fullscreen', isFs);
        };
        document.addEventListener('fullscreenchange', handleFullscreenChange);
        return () => {
            document.removeEventListener('fullscreenchange', handleFullscreenChange);
            document.body.classList.remove('bakers-fullscreen');
        };
    }, []);

    // Auto-enter fullscreen if previously used
    useEffect(() => {
        if (localStorage.getItem('bakersV2Fullscreen') === 'true') {
            document.documentElement.requestFullscreen?.().catch(() => {});
        }
    }, []);

    function toggleFullscreen() {
        if (!document.fullscreenElement) {
            document.documentElement.requestFullscreen?.();
            localStorage.setItem('bakersV2Fullscreen', 'true');
        } else {
            document.exitFullscreen?.();
            localStorage.setItem('bakersV2Fullscreen', 'false');
        }
    }

    // Detect new orders and show notification alert
    useEffect(() => {
        const today = getTodayKey();
        const detected = [];

        cakeOrders.forEach(order => {
            const orderDate = order.date.split('T')[0];
            const isNewPendingOrder =
                !previousCakeIds.current.has(order.id) &&
                orderDate === today &&
                !order.ready;

            if (isNewPendingOrder) {
                detected.push({ ...order, type: 'cake' });
            }
            previousCakeIds.current.add(order.id);
        });

        breadOrders.forEach(order => {
            const orderDate = order.date.split('T')[0];
            const isNewPendingOrder =
                !previousBreadIds.current.has(order.id) &&
                orderDate === today &&
                order.status === 'pending';

            if (isNewPendingOrder) {
                detected.push({ ...order, type: 'bread' });
            }
            previousBreadIds.current.add(order.id);
        });

        if (detected.length > 0) {
            playNotificationSound();
            setNewOrders(detected);
            setShowAlert(true);
        }
    }, [cakeOrders, breadOrders]);

    // Initialize tab and date from URL params
    useEffect(() => {
        const initialTab = searchParams.get('tab');
        const initialDate = searchParams.get('date');
        if (initialTab === 'cakes' || initialTab === 'breads') {
            setActiveTab(initialTab);
        }
        if (initialDate && /^\d{4}-\d{2}-\d{2}$/.test(initialDate)) {
            setSelectedDate(initialDate);
        }
    }, [searchParams]);

    function handleTabChange(tab) {
        setActiveTab(tab);
        setSearchParams({ tab, date: selectedDate });
    }

    function handleDateChange(date) {
        setSelectedDate(date);
        setSearchParams({ tab: activeTab, date });
    }

    const handleReload = useCallback(() => {
        // Force re-render by toggling a dummy state - hooks handle the actual refresh
        window.location.reload();
    }, []);

    // Calculate pending order counts per day (for day filter badges)
    const cakeCountsByDate = useMemo(
        () => getOrderCountsByDate(cakeOrders, order => !order.ready),
        [cakeOrders]
    );

    const breadCountsByDate = useMemo(
        () => getOrderCountsByDate(breadOrders, order => order.status === 'pending'),
        [breadOrders]
    );

    // Filter orders for selected date
    const filteredCakes = useMemo(
        () => filterOrdersByDate(cakeOrders, selectedDate, order => !order.ready),
        [cakeOrders, selectedDate]
    );

    const filteredBreads = useMemo(
        () => filterOrdersByDate(breadOrders, selectedDate, order => order.status === 'pending'),
        [breadOrders, selectedDate]
    );

    // Calculate pending counts for selected date
    const pendingCakes = filteredCakes.filter(order => !order.ready).length;
    const pendingBreads = filteredBreads.filter(order => order.status === 'pending').length;

    return (
        <div className="bakers-view-v2">
            <header className="bakers-header-v2">
                <div className="header-row">
                    <TabBarV2 activeTab={activeTab} onTabChange={handleTabChange} />
                    <button
                        className="fullscreen-button"
                        onClick={toggleFullscreen}
                        title={isFullscreen ? 'Salir de pantalla completa' : 'Pantalla completa'}
                    >
                        {isFullscreen ? 'SALIR' : 'PANTALLA COMPLETA'}
                    </button>
                    <Link to="/panaderos-legacy" className="legacy-link-button">
                        versión anterior
                    </Link>
                </div>
                <DayFilterV2
                    selectedDate={selectedDate}
                    onDateChange={handleDateChange}
                    orderCounts={activeTab === 'cakes' ? cakeCountsByDate : breadCountsByDate}
                />
            </header>

            <main className="bakers-content-v2">
                {activeTab === 'cakes' && (
                    <div className="orders-container-v2">
                        <ProgressHeaderV2
                            pending={pendingCakes}
                            total={filteredCakes.length}
                            onReload={handleReload}
                            label={pendingCakes === 1 ? 'pastel' : 'pasteles'}
                        />
                        {filteredCakes.length === 0 ? (
                            <div className="empty-state-v2">
                                <p className="empty-text-v2">No hay pedidos de pasteles para este día</p>
                            </div>
                        ) : (
                            <div className={orderCardStyles['orders-grid-v2']}>
                                {filteredCakes.map(order => (
                                    <CakeCardV2 key={order.id} order={order} />
                                ))}
                            </div>
                        )}
                    </div>
                )}

                {activeTab === 'breads' && (
                    <div className="orders-container-v2">
                        <ProgressHeaderV2
                            pending={pendingBreads}
                            total={filteredBreads.length}
                            onReload={handleReload}
                            label={pendingBreads === 1 ? 'pan' : 'panes'}
                        />
                        {filteredBreads.length === 0 ? (
                            <div className="empty-state-v2">
                                <p className="empty-text-v2">No hay pedidos de pan para este día</p>
                            </div>
                        ) : (
                            <div className={orderCardStyles['orders-grid-v2']}>
                                {filteredBreads.map(order => (
                                    <BreadCardV2 key={order.id} order={order} />
                                ))}
                            </div>
                        )}
                    </div>
                )}
            </main>

            {showAlert && (
                <NewOrderAlertV2
                    orders={newOrders}
                    onClose={() => setShowAlert(false)}
                />
            )}
        </div>
    );
}
